import {
  _createContact,
  _extractContacts,
  _updateContactCategories,
  _uploadContactRequest,
} from '@/api/address-book/mutations';
import {
  ConnectionType,
  ContactFieldType,
  ContactReferenceType,
  type ContactReferenceTypeValue,
} from '@/api/address-book/types';
import { _getBffContactById, _getBffContacts } from '@/api/address-book-bff/queries';
import { _getCountries } from '@/api/lookup/queries';
import { LocationList } from '@/api/lookup/types';
import { queryClient } from '@/app/providers/react-query-provider';
import { splitStreetLines } from '@/utils/format-contact-address';
import { uploadToS3 } from '@/utils/upload-to-s3';

import { resolveCountryId } from './resolve-country-id';
import type { ContactData } from './types';

async function extractedCountryId(value: string | null): Promise<string> {
  if (!value?.trim()) return '';
  try {
    const countries = await queryClient.fetchQuery(
      _getCountries({ list: LocationList.ADDRESS, includeRegions: true }),
    );
    return resolveCountryId(countries, value);
  } catch {
    return '';
  }
}

export async function ocrBusinessCard(file: File): Promise<Partial<ContactData>> {
  const uploadReq = await _uploadContactRequest({
    filename: file.name,
    contentType: file.type,
    size: file.size,
  });

  await uploadToS3({
    uploadUrl: uploadReq.uploadUrl,
    uploadHeaders: uploadReq.uploadHeaders as Record<string, string>,
    file,
    fileId: uploadReq.fileId,
  });

  const result = await _extractContacts({ fileId: uploadReq.fileId });
  const countryId = await extractedCountryId(result.country);

  return {
    name: result.fullName ?? undefined,
    emails: result.email ? [result.email] : undefined,
    phones: result.phone ? [result.phone] : undefined,
    jobTitle: result.jobTitle ?? undefined,
    organizationName: result.company ?? undefined,
    addresses:
      result.country || result.city || result.address
        ? [
            {
              country: countryId,
              city: result.city ?? '',
              ...splitStreetLines(result.address),
            },
          ]
        : undefined,
    websites: result.website ? [result.website] : undefined,
  };
}

const NAME_SEARCH_PAGE_SIZE = 250;

export async function findContactIdByName(
  name: string,
  referenceType: ContactReferenceTypeValue | ContactReferenceTypeValue[],
  isStale: () => boolean = () => false,
): Promise<string | undefined> {
  const search = name.trim();
  const target = search.toLowerCase();
  let totalPages = 1;

  for (let page = 1; page <= totalPages; page += 1) {
    if (isStale()) return undefined;
    const found = await queryClient.fetchQuery(
      _getBffContacts({ search, referenceType, page, limit: NAME_SEARCH_PAGE_SIZE }),
    );
    const match = found.contacts.find((c) => c.name?.trim().toLowerCase() === target);
    if (match) {
      return match.id;
    }
    totalPages = found.pagination.totalPages;
  }

  return undefined;
}

/**
 * Resolves a typed organization name to an Organization contact id, creating it when it does not
 * exist yet. `POST /contacts` with referenceType=Organization does NOT find-or-create — it throws
 * DuplicateOrganizationNameError — so the lookup happens here, and a lost create race falls back to
 * the winner rather than failing the whole contact.
 */
async function resolveOrganizationId(name: string): Promise<string> {
  const trimmed = name.trim();
  const findExisting = () => findContactIdByName(trimmed, ContactReferenceType.Organization);

  const existing = await findExisting();
  if (existing) return existing;

  try {
    const created = await _createContact({
      type: ConnectionType.Manual,
      referenceType: ContactReferenceType.Organization,
      name: trimmed,
      categoryNodeIds: [],
    });
    return created.id;
  } catch (error) {
    const winner = await findExisting();
    if (winner) return winner;
    throw error;
  }
}

export async function createContact(
  payload: Partial<ContactData> & {
    categoryIds: string[];
    qrReferenceId?: string;
    qrContactCode?: string;
  },
): Promise<{ id: string; isNew: boolean }> {
  if (payload.qrReferenceId) {
    const contact = await _createContact({
      type: ConnectionType.Direct,
      referenceType: ContactReferenceType.Individual,
      referenceId: payload.qrReferenceId,
      contactCode: payload.qrContactCode ?? null,
      name: payload.name ?? null,
      notes: null,
      categoryNodeIds: payload.categoryIds,
    });
    return { id: contact.id, isNew: contact.isNew };
  }

  // Disjoint email model (GP-3604): the first email is the identity/merge key and travels in the
  // top-level `email` field; any further emails are ADDITIONAL and go into values[] as Email entries.
  const organizationId =
    payload.organizationId ||
    (payload.contactType !== 'organization' && payload.organizationName?.trim()
      ? await resolveOrganizationId(payload.organizationName)
      : undefined);

  const [primaryEmail, ...additionalEmails] = (payload.emails ?? [])
    .map((email) => email.trim())
    .filter(Boolean);
  const emailValues = additionalEmails.map((email) => ({
    fieldType: ContactFieldType.Email,
    label: null,
    value: email,
  }));
  const phoneValues = (payload.phones ?? []).map((phone) => ({
    fieldType: ContactFieldType.Phone,
    label: null,
    value: phone,
  }));
  const addressValues = (payload.addresses ?? []).map((address) => ({
    fieldType: ContactFieldType.Address,
    label: null,
    address: { ...address, street2: address.street2?.trim() || null },
  }));
  const websiteValues = (payload.websites ?? []).map((website) => ({
    fieldType: ContactFieldType.Website,
    label: null,
    value: website,
  }));
  const socialValues = (payload.socialLinks ?? []).map((link) => ({
    fieldType: ContactFieldType.Social,
    label: link.platform,
    value: link.value,
  }));

  const contact = await _createContact({
    type: ConnectionType.Manual,
    referenceType:
      payload.contactType === 'organization'
        ? ContactReferenceType.Organization
        : ContactReferenceType.External,
    email: primaryEmail ?? null,
    name: payload.name?.trim() ?? null,
    jobTitle: payload.jobTitle?.trim() ?? null,
    organizationId: organizationId || null,
    company: null,
    notes: payload.notes?.trim() ?? null,
    iconId: payload.iconId ?? null,
    categoryNodeIds: payload.categoryIds,
    values: [...emailValues, ...phoneValues, ...addressValues, ...websiteValues, ...socialValues],
  });

  return { id: contact.id, isNew: contact.isNew };
}

export async function addCategoriesToContact(
  contactId: string,
  categoryIds: string[],
): Promise<void> {
  if (categoryIds.length === 0) return;
  const { queryKey, queryFn } = _getBffContactById(contactId);
  const contact = await queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });
  const categoryNodeIds = [...new Set([...(contact.categoryNodeIds ?? []), ...categoryIds])];
  await _updateContactCategories({ id: contactId, body: { categoryNodeIds } });
}
